// Chatbularis dentro de Word: el panel lateral del complemento.
//
// Es otra página y no el chat de /asistente encogido: ese está pensado para
// pantalla completa, y el panel de Word mide unos 350 px. Lo que sí es el
// mismo es todo lo de atrás —la consulta, los modos, el saldo, la biblioteca y
// el estilo del estudio—: este panel solo agrega lo propio de Word, que es
// preguntar sobre lo seleccionado y devolver la respuesta al documento.
//
// Vive en una sola dirección para todos los estudios (tabularis.cl/word),
// porque es la que va en el manifiesto. Por eso el estudio no sale del
// subdominio como en el panel: se pide al entrar.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LogoChatbularisSVG } from '../../components/Icons';
import { api, ApiError, setToken, getToken, urlEstudio } from '../../utils/api';
import * as IA from '../../utils/asistente';
import { enWord, leerSeleccion, leerDocumento, alCambiarSeleccion, insertar, MAX_TEXTO_WORD } from '../../utils/word';
import './WordApp.css';

type PerfilWord = { nombre: string; estudio: string; subdominio: string };

const CLAVE_PERFIL = 'word_perfil';
// La misma que usa la landing: quien ya entró a su estudio desde tabularis.cl
// no tiene que volver a escribirlo.
const ULTIMO_ESTUDIO = 'tabularis_ultimo_estudio';
const CLAVE_MODO = 'word_razonamiento';
const CLAVE_FUENTE = 'word_fuente';

/** Qué parte del documento va con la pregunta. */
type Fuente = 'seleccion' | 'documento' | 'nada';
const FUENTES: { id: Fuente; etiqueta: string }[] = [
  { id: 'seleccion', etiqueta: 'Selección' },
  { id: 'documento', etiqueta: 'Todo el documento' },
  { id: 'nada', etiqueta: 'Nada' },
];
/** Lo de Word que acompaña una pregunta. */
type TextoWord = { seleccion?: string; documento?: string };

const leer = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const escribir = (k: string, v: string | null) => {
  try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* modo privado */ }
};

function perfilGuardado(): PerfilWord | null {
  try {
    const p = JSON.parse(leer(CLAVE_PERFIL) || 'null');
    return p && getToken() ? p : null;
  } catch { return null; }
}

const palabras = (t: string) => (t.match(/\S+/g) || []).length;
const clp = (n: number) => '$' + Math.round(n).toLocaleString('es-CL');

const WordApp: React.FC = () => {
  const [perfil, setPerfil] = useState<PerfilWord | null>(perfilGuardado);
  const [dentroDeWord, setDentroDeWord] = useState<boolean | null>(null);

  useEffect(() => { document.title = 'Chatbularis para Word'; }, []);
  useEffect(() => { enWord().then(setDentroDeWord); }, []);

  // El cliente de la API avisa cuando el servidor rechaza la sesión (caducó o
  // la cuenta se desvinculó) y ya borró el token.
  useEffect(() => {
    const salir = () => { escribir(CLAVE_PERFIL, null); setPerfil(null); };
    window.addEventListener('tabularis:sesion', salir);
    return () => window.removeEventListener('tabularis:sesion', salir);
  }, []);

  const salir = () => {
    setToken(null);
    escribir(CLAVE_PERFIL, null);
    setPerfil(null);
  };

  if (!perfil) return <Ingreso alEntrar={setPerfil} />;
  return <Chat perfil={perfil} dentroDeWord={dentroDeWord} alSalir={salir} />;
};

/* ── Ingreso ──────────────────────────────────────────────────────────── */

const Ingreso: React.FC<{ alEntrar: (p: PerfilWord) => void }> = ({ alEntrar }) => {
  const [subdominio, setSubdominio] = useState(leer(ULTIMO_ESTUDIO) || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [entrando, setEntrando] = useState(false);

  const entrar = async (e: React.FormEvent) => {
    e.preventDefault();
    const sub = subdominio.trim().toLowerCase().replace(/\.tabularis\.cl$/, '');
    if (!sub || !email.trim() || !password) { setError('Completa los tres campos.'); return; }
    setError('');
    setEntrando(true);
    try {
      const r = await api<{ token: string; usuario: { nombre: string }; tenant?: { nombre: string; subdominio: string } }>(
        '/auth/login', { method: 'POST', auth: false, body: { subdominio: sub, email: email.trim(), password } },
      );
      setToken(r.token);
      const p: PerfilWord = { nombre: r.usuario.nombre, estudio: r.tenant?.nombre || sub, subdominio: r.tenant?.subdominio || sub };
      escribir(CLAVE_PERFIL, JSON.stringify(p));
      escribir(ULTIMO_ESTUDIO, p.subdominio);
      alEntrar(p);
    } catch (err: any) {
      // El bloqueo por suscripción trae su propio mensaje; el pago se hace en
      // el navegador, no desde un panel de Word.
      setError(err?.status === 401 ? 'El estudio, el correo o la contraseña no coinciden.' : err?.message || 'No se pudo entrar.');
    } finally {
      setEntrando(false);
    }
  };

  return (
    <div className="cw-ingreso">
      <div className="cw-ingreso-marca">
        <LogoChatbularisSVG size={44} />
        <div>
          <div className="cw-ingreso-titulo">Chatbularis</div>
          <div className="cw-ingreso-sub">para Word</div>
        </div>
      </div>
      <p className="cw-ingreso-texto">Entra con la misma cuenta de tu estudio en Tabularis.</p>
      <form onSubmit={entrar} className="cw-form">
        <label>
          Estudio
          <div className="cw-sub">
            <input value={subdominio} onChange={(e) => setSubdominio(e.target.value)} placeholder="tuestudio" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            <span>.tabularis.cl</span>
          </div>
        </label>
        <label>
          Correo
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
        </label>
        <label>
          Contraseña
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        </label>
        {error && <div className="cw-error">{error}</div>}
        <button type="submit" className="cw-boton cw-boton--primario" disabled={entrando}>{entrando ? 'Entrando…' : 'Entrar'}</button>
      </form>
      <p className="cw-nota">¿Olvidaste la contraseña? Recupérala desde tu estudio en el navegador y vuelve a entrar acá.</p>
    </div>
  );
};

/* ── Chat ─────────────────────────────────────────────────────────────── */

type Confirmar = IA.ConfirmacionCaso & { pregunta: string; word: TextoWord };

const Chat: React.FC<{ perfil: PerfilWord; dentroDeWord: boolean | null; alSalir: () => void }> = ({ perfil, dentroDeWord, alSalir }) => {
  const [mensajes, setMensajes] = useState<IA.MensajeIA[]>([]);
  const [convId, setConvId] = useState<string | undefined>();
  const [input, setInput] = useState('');
  const [modo, setModo] = useState<IA.Razonamiento>(() => {
    const v = leer(CLAVE_MODO);
    return IA.RAZONAMIENTOS.some((r) => r.id === v) ? (v as IA.Razonamiento) : IA.RAZONAMIENTO_POR_DEFECTO;
  });
  const [seleccion, setSeleccion] = useState('');
  const [fuente, setFuente] = useState<Fuente>(() => {
    const v = leer(CLAVE_FUENTE);
    return FUENTES.some((f) => f.id === v) ? (v as Fuente) : 'seleccion';
  });
  const elegirFuente = (f: Fuente) => { setFuente(f); escribir(CLAVE_FUENTE, f); };
  const [cargando, setCargando] = useState(false);
  const [enVivo, setEnVivo] = useState<{ texto: string; pensando: number } | null>(null);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [saldo, setSaldo] = useState<IA.SaldoIA | null>(null);
  const [confirmar, setConfirmar] = useState<Confirmar | null>(null);
  const finRef = useRef<HTMLDivElement>(null);
  const convRef = useRef<string | undefined>();
  convRef.current = convId;

  const recargarSaldo = useCallback(() => { IA.estadoSaldo().then(setSaldo).catch(() => undefined); }, []);
  useEffect(recargarSaldo, [recargarSaldo]);

  // La selección se sigue en vivo: el abogado ve qué va a mandar antes de
  // preguntar, y no hay botón de "tomar selección" que olvidar.
  useEffect(() => {
    if (!dentroDeWord) return;
    const mirar = () => { leerSeleccion().then(setSeleccion).catch(() => undefined); };
    mirar();
    alCambiarSeleccion(mirar);
  }, [dentroDeWord]);

  useEffect(() => { finRef.current?.scrollIntoView({ block: 'end' }); }, [mensajes, enVivo?.texto, confirmar]);

  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(''), 2500);
    return () => clearTimeout(t);
  }, [aviso]);

  const elegirModo = (r: IA.Razonamiento) => { setModo(r); escribir(CLAVE_MODO, r); };

  const nueva = () => {
    if (cargando) return;
    setMensajes([]);
    setConvId(undefined);
    setError('');
    setConfirmar(null);
  };

  /** La conexión se cortó a mitad de camino: la consulta puede seguir en el
   *  servidor y quedar guardada (y cobrada). Se la va a buscar antes de darla
   *  por perdida, igual que en /asistente. */
  const esperarRespuesta = async (id: string, teniamos: number): Promise<boolean> => {
    const limite = Date.now() + 20 * 60 * 1000;
    while (Date.now() < limite) {
      if (convRef.current !== id) return false;
      try {
        const conv = await IA.obtenerConversacion(id);
        if ((conv.mensajes ?? []).filter((m) => m.rol === 'assistant').length > teniamos) {
          setMensajes(conv.mensajes ?? []);
          recargarSaldo();
          return true;
        }
        if (!conv.enCurso) return false;
      } catch { /* sin red todavía */ }
      await new Promise((ok) => setTimeout(ok, 4000));
    }
    return false;
  };

  const enviar = async (pregunta: string, word: TextoWord, caso: { casoId?: string; sinCaso?: boolean } = {}) => {
    if (!pregunta.trim() || cargando) return;
    setError('');
    setConfirmar(null);
    setInput('');
    setCargando(true);
    const teniamos = mensajes.filter((m) => m.rol === 'assistant').length;
    setMensajes((m) => [...m, {
      rol: 'user', texto: pregunta,
      ...(word.seleccion ? { seleccion: word.seleccion } : {}),
      ...(word.documento ? { documentoWord: word.documento.length } : {}),
      ts: new Date().toISOString(),
    }]);
    setEnVivo({ texto: '', pensando: 0 });
    try {
      const r = await IA.consultar(
        pregunta, convId, modo,
        {
          onTexto: (t) => setEnVivo((v) => ({ texto: (v?.texto ?? '') + t, pensando: v?.pensando ?? 0 })),
          onPensando: (s) => setEnVivo((v) => ({ texto: v?.texto ?? '', pensando: s })),
        },
        caso,
        { ...word, desde: 'word' },
      );
      setConvId(r.conversacionId);
      setMensajes((m) => [...m, {
        rol: 'assistant', texto: r.respuesta, fuentes: r.fuentes, razonamiento: r.razonamiento,
        ...(r.cobradoClp != null ? { cobradoClp: r.cobradoClp } : {}),
        ...(r.noLeidos?.length ? { noLeidos: r.noLeidos } : {}),
        ts: new Date().toISOString(),
      }]);
      if (r.saldo) setSaldo(r.saldo); else recargarSaldo();
    } catch (e: any) {
      const pide = IA.pideConfirmarCaso(e);
      if (pide) {
        setMensajes((m) => m.slice(0, -1));
        setConfirmar({ ...pide, pregunta, word });
        return;
      }
      const id = convRef.current;
      if (id && (IA.seCortoLaConexion(e) || IA.hayConsultaEnCurso(e))) {
        setEnVivo({ texto: '', pensando: -1 });
        if (await esperarRespuesta(id, teniamos)) return;
      }
      setMensajes((m) => m.slice(0, -1));
      setInput(pregunta);
      setError(
        IA.seCortoLaConexion(e) || IA.hayConsultaEnCurso(e)
          ? 'Se cortó la conexión y la respuesta no alcanzó a guardarse. Tu pregunta quedó en el cuadro para enviarla de nuevo.'
          : e instanceof ApiError || e?.message ? e.message : 'Chatbularis no pudo responder. Intenta de nuevo.',
      );
      if (IA.sinSaldo(e)) recargarSaldo();
    } finally {
      setCargando(false);
      setEnVivo(null);
    }
  };

  const seleccionVigente = dentroDeWord && fuente === 'seleccion' && seleccion ? seleccion : undefined;
  const alEnviar = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!input.trim() || cargando) return;
    setError('');
    let word: TextoWord = {};
    try {
      // El documento se lee al enviar y no antes: el abogado puede haberlo
      // cambiado desde la pregunta anterior, y lo que vale es lo de ahora.
      if (dentroDeWord && fuente === 'documento') {
        const documento = await leerDocumento();
        if (!documento) { setError('El documento está vacío.'); return; }
        word = { documento };
      } else if (seleccionVigente) {
        // La selección también se vuelve a leer: el aviso de cambio puede
        // llegar tarde, y mandar la de hace un momento sería preguntar por otro
        // párrafo.
        word = { seleccion: (await leerSeleccion()) || seleccionVigente };
      }
    } catch {
      setError('No se pudo leer el documento de Word. Intenta de nuevo.');
      return;
    }
    const largo = (word.documento ?? word.seleccion ?? '').length;
    if (largo > MAX_TEXTO_WORD) {
      // Se avisa acá, antes de mandar: el servidor lo rechazaría igual, pero
      // así queda claro qué hacer y no se pierde la pregunta.
      setError(
        `${word.documento ? 'El documento' : 'La selección'} tiene ${largo.toLocaleString('es-CL')} caracteres y una consulta admite hasta ${MAX_TEXTO_WORD.toLocaleString('es-CL')} (unas 80 páginas). ` +
          'Selecciona la parte sobre la que quieres preguntar.',
      );
      return;
    }
    void enviar(input, word);
  };

  const enDocumento = async (texto: string, como: 'reemplazar' | 'despues') => {
    setError('');
    try {
      await insertar(texto, como);
      setAviso(como === 'reemplazar' ? 'Reemplazado en el documento' : 'Insertado en el documento');
    } catch (e: any) {
      setError(e?.message || 'No se pudo escribir en el documento.');
    }
  };

  const copiar = async (texto: string) => {
    try { await navigator.clipboard.writeText(texto); setAviso('Copiado'); }
    catch { setError('No se pudo copiar.'); }
  };

  const abrirEstudio = (ruta: string) => window.open(urlEstudio(perfil.subdominio) + ruta, '_blank', 'noopener');

  return (
    <div className="cw-root">
      <header className="cw-cabecera">
        <LogoChatbularisSVG size={26} />
        <div className="cw-cabecera-texto">
          <div className="cw-cabecera-nombre">{perfil.estudio}</div>
          <div className="cw-cabecera-sub">
            {perfil.nombre}
            {saldo && <> · {saldo.sinTope ? 'sin tope' : `saldo ${clp(saldo.saldoClp)}`}</>}
          </div>
        </div>
        <button className="cw-icono" onClick={nueva} disabled={cargando} title="Nueva conversación">＋</button>
        <button className="cw-icono cw-icono--texto" onClick={alSalir} title="Cerrar sesión en este Word">Salir</button>
      </header>

      <main className="cw-mensajes">
        {dentroDeWord === false && (
          <div className="cw-aviso-fuera">Estás fuera de Word: puedes preguntar, pero no leer ni insertar en un documento.</div>
        )}
        {!mensajes.length && !confirmar && (
          <div className="cw-vacio">
            <p><strong>Selecciona un texto</strong> y pregunta sobre él: «¿esta cláusula es válida?», «corrige la redacción».</p>
            <p>Con <strong>Todo el documento</strong>, Chatbularis lee el escrito entero: «revisa errores y contradicciones», «¿falta algo en el petitorio?».</p>
            <p>La respuesta se inserta en el documento con un clic.</p>
          </div>
        )}
        {mensajes.map((m, i) => m.rol === 'user' ? (
          <div key={i} className="cw-msg cw-msg--yo">
            {m.seleccion && <blockquote className="cw-cita" title={m.seleccion}>{m.seleccion}</blockquote>}
            {!!m.documentoWord && <div className="cw-con-documento">Con el documento completo</div>}
            <div>{m.texto}</div>
          </div>
        ) : (
          <div key={i} className="cw-msg cw-msg--ia">
            <div className="cw-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.texto}</ReactMarkdown></div>
            {!!m.noLeidos?.length && (
              <div className="cw-noleidos">No leyó: {m.noLeidos.map((n) => `${n.nombre} (${n.motivo})`).join('; ')}</div>
            )}
            <div className="cw-acciones">
              {dentroDeWord && (
                <>
                  <button className="cw-boton cw-boton--primario" onClick={() => enDocumento(m.texto, 'despues')} title="Escribe la respuesta a continuación de lo seleccionado, o en el cursor">Insertar</button>
                  <button className="cw-boton" onClick={() => enDocumento(m.texto, 'reemplazar')} title="Pone la respuesta en lugar del texto seleccionado ahora">Reemplazar selección</button>
                </>
              )}
              <button className="cw-boton" onClick={() => copiar(m.texto)}>Copiar</button>
              {m.cobradoClp != null && <span className="cw-cobro">{clp(m.cobradoClp)}</span>}
            </div>
          </div>
        ))}
        {enVivo && (
          <div className="cw-msg cw-msg--ia">
            {enVivo.texto
              ? <div className="cw-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{enVivo.texto}</ReactMarkdown></div>
              : <div className="cw-pensando">{enVivo.pensando < 0 ? 'Se cortó la conexión; buscando la respuesta…' : `Pensando${enVivo.pensando ? ` · ${enVivo.pensando} s` : '…'}`}</div>}
          </div>
        )}
        {confirmar && (
          <div className="cw-confirmar">
            <div className="cw-confirmar-titulo">{confirmar.mensaje}</div>
            {confirmar.candidatos.map((c) => (
              <button key={c.id} className="cw-caso" onClick={() => enviar(confirmar.pregunta, confirmar.word, { casoId: c.id })}>
                <strong>{c.cliente}</strong>
                <span>{[c.codigo, c.tema, c.rolCaso && `rol ${c.rolCaso}`].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
            <div className="cw-acciones">
              <button className="cw-boton" onClick={() => enviar(confirmar.pregunta, confirmar.word, { sinCaso: true })}>No es de ningún caso</button>
              <button className="cw-boton" onClick={() => { setInput(confirmar.pregunta); setConfirmar(null); }}>Cancelar</button>
            </div>
          </div>
        )}
        {error && (
          <div className="cw-error">
            {error}
            {saldo?.agotado && <> <button className="cw-enlace" onClick={() => abrirEstudio('/asistente')}>Cargar saldo</button></>}
          </div>
        )}
        <div ref={finRef} />
      </main>

      <form className="cw-compositor" onSubmit={alEnviar}>
        {dentroDeWord && (
          <>
            <div className="cw-fuentes" role="radiogroup" aria-label="Qué lee Chatbularis">
              <span>Lee:</span>
              {FUENTES.map((f) => (
                <button type="button" key={f.id} role="radio" aria-checked={fuente === f.id}
                  className={'cw-fuente' + (fuente === f.id ? ' cw-fuente--activa' : '')} onClick={() => elegirFuente(f.id)}>
                  {f.etiqueta}
                </button>
              ))}
            </div>
            {fuente === 'seleccion' && (
              <div className={'cw-seleccion' + (seleccion ? '' : ' cw-seleccion--off')}>
                {seleccion
                  ? <>{palabras(seleccion)} {palabras(seleccion) === 1 ? 'palabra' : 'palabras'}: «{seleccion.slice(0, 80)}{seleccion.length > 80 ? '…' : ''}»</>
                  : 'No hay texto seleccionado: la pregunta va sola.'}
              </div>
            )}
            {fuente === 'documento' && (
              <div className="cw-seleccion">Lee el escrito completo, tal como esté al enviar.</div>
            )}
          </>
        )}
        <div className="cw-modos">
          {IA.RAZONAMIENTOS.map((r) => (
            <button type="button" key={r.id} className={'cw-modo' + (modo === r.id ? ' cw-modo--activo' : '')} onClick={() => elegirModo(r.id)} title={r.detalle}>
              {r.etiqueta}
            </button>
          ))}
        </div>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void alEnviar(); }}
          placeholder={seleccionVigente ? '¿Qué quieres hacer con el texto seleccionado?' : dentroDeWord && fuente === 'documento' ? '¿Qué quieres saber del documento?' : 'Escribe tu consulta…'}
          rows={3}
          disabled={cargando}
        />
        <button type="submit" className="cw-boton cw-boton--primario cw-enviar" disabled={cargando || !input.trim()}>
          {cargando ? 'Respondiendo…' : 'Enviar'}
        </button>
      </form>
      {aviso && <div className="cw-toast">{aviso}</div>}
    </div>
  );
};

export default WordApp;
